/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { IView, IViewInfo, TViewComponent } from "../../view/IView";
import { Layout } from "../../ui/components/Layout/Layout";
import { LayoutState } from "../../ui/components/Layout/LayoutState";
import { LAYOUT_TYPE } from "../../ui/components/Layout/LayoutTypes";
import { ARROW_POSITION } from "../../ui/components/Toolbar/Toolbar";
import { ToolbarTitle } from "../../ui/components/Toolbar/ToolbarTitle";
import { ToolbarSpacer } from "../../ui/components/Toolbar/ToolbarSpacer";
import { ToolbarButton } from "../../ui/components/Toolbar/ToolbarButton";
import { OutlineContent } from "./OutlineContent";
import { DataExplorerContent } from "./DataExplorerContent";
import { Breadcrumbs } from "../../ui/components/Breadcrumbs/Breadcrumbs";
import { ToolbarCommandButton } from "../../ui/components/Toolbar/ToolbarCommandButton";
import { TestModuleConsoleToolbar } from "./TestModuleView/components/TestModuleConsoleToolbar";
import { TestModuleConsoleContent } from "./TestModuleView/components/TestModuleConsoleContent";
import { TestModuleInspector } from "./TestModuleView/components/TestModuleInspector";
import { TestModulePrimaryPanel } from "./TestModuleView/components/TestModulePrimaryPanel";
import { ViewPreview } from "../../ui/components/ViewPreview";
import { IEditor } from "../../IEditor";
import { TRootComponentListSchema } from "@adapptio/lib-components";
import { EditorRenderContext } from "../../runtime/EditorRenderContext";
import { EditorRoutingManager } from "../../runtime/EditorRoutingManager";
import { RUNTIME_CONTEXT_MODE } from "@adapptio/lib-blueprint";

interface ITestViewParams {
	label: string;
}

export class TestView implements IView<ITestViewParams> {
	public label: string;
	private renderWhenNotActive: boolean;

	private testRoutingManager: EditorRoutingManager;
	private testRenderContext: EditorRenderContext<TRootComponentListSchema>;

	public constructor(private dc: IEditor) {
		this.testRoutingManager = dc.get("runtimeService").createRoutingManager();

		this.testRenderContext = dc.get("runtimeService").createRenderContext<TRootComponentListSchema>({
			mode: RUNTIME_CONTEXT_MODE.NORMAL,
			mediaResolution: "screenBase",
			routingManager: this.testRoutingManager,
			getComponentListSpec: (spec) => spec,
			eventLogFn: () => {
				/* dummy */
			},
			rootView: {
				viewName: "main",
				viewParams: {}
			}
		});
	}

	public async init(params: ITestViewParams): Promise<void> {
		this.label = params.label;

		this.testRenderContext.getRuntimeContext().render();

		return new Promise((resolve, reject) => {
			setTimeout(() => {
				// reject(new Error("Test error"));
				resolve();
			}, 40);
		});
	}

	public dispose(): void {
		this.testRenderContext.destroy();
	}

	public getViewInfo(): IViewInfo {
		return {
			icon: "mdi/apps",
			label: this.label,
			canClose: true,
			isModified: false,
			renderWhenNotActive: true
		};
	}

	public getTestRenderContext(): EditorRenderContext<TRootComponentListSchema> {
		return this.testRenderContext;
	}
}

export const TestViewComponent: TViewComponent<TestView> = (props) => {
	const [ activeButtons, setActiveButtons ] = React.useState(new Set());
	const toggleButton = React.useCallback((buttonId: string) => {
		setActiveButtons((prevState) => {
			const newState = new Set(prevState);
			return !newState.delete(buttonId) ? newState.add(buttonId) : newState;
		});
	}, []);

	const breadcrumbs = [
		{ label: "First", onClick: () => console.log("Breadcrumb click: Common") },
		{ label: "Second", onClick: () => console.log("Breadcrumb click: Common") },
		{ label: "Common hello how u doin", onClick: () => console.log("Breadcrumb click: Common") },
		{ label: "Whatever", onClick: () => console.log("Breadcrumb click: Common") },
		{ label: "Loop", onClick: () => console.log("Breadcrumb click: Common") },
		{ label: "Organizations", onClick: () => console.log("Breadcrumb click: Common") },
		{ label: "jjjggggTTTRRR", onClick: () => console.log("Breadcrumb click: Common") },
		{ label: "Main View" }
	];

	const layoutState = useMemo(() => {
		return new LayoutState({
			root: {
				type: LAYOUT_TYPE.ROW,
				id: "layout",
				children: [
					{
						type: LAYOUT_TYPE.COLUMN,
						id: "left",
						size: "250px",
						children: [
							{
								type: LAYOUT_TYPE.PANEL,
								id: "outline",
								isExpandable: true,
								size: 30,
								arrowPosition: ARROW_POSITION.LEFT,
								toolbarComponent: "OutlineToolbar",
								contentComponent: "OutlineContent"
							},
							{
								type: LAYOUT_TYPE.SPLITTER,
								id: "leftSplitter"
							},
							{
								type: LAYOUT_TYPE.PANEL,
								id: "dataExplorer",
								isExpandable: true,
								size: 60,
								arrowPosition: ARROW_POSITION.LEFT,
								toolbarComponent: "DataExplorerToolbar",
								contentComponent: "DataExplorerContent"
							}
						]
					},
					{
						type: LAYOUT_TYPE.SPLITTER,
						id: "layoutSplitterLeft"
					},
					{
						type: LAYOUT_TYPE.COLUMN,
						id: "main",
						children: [
							{
								type: LAYOUT_TYPE.PANEL,
								id: "preview",
								isExpandable: false,
								toolbarComponent: "PreviewToolbar",
								contentComponent: "PreviewContent"
							},
							{
								type: LAYOUT_TYPE.SPLITTER,
								id: "mainSplitter1"
							},
							{
								type: LAYOUT_TYPE.PANEL,
								id: "primaryPanel",
								isExpandable: false,
								toolbarComponent: "PrimaryPanelToolbar",
								contentComponent: "PrimaryPanel"
							},
							{
								type: LAYOUT_TYPE.SPLITTER,
								id: "mainSplitter2"
							},
							{
								type: LAYOUT_TYPE.PANEL,
								id: "toasts",
								isExpandable: false,
								toolbarComponent: "ToastsToolbar",
								contentComponent: "Empty",
								size: 6
							},
							{
								type: LAYOUT_TYPE.SPLITTER,
								id: "mainSplitter3"
							},
							{
								type: LAYOUT_TYPE.PANEL,
								id: "console",
								isExpandable: true,
								defaultExpanded: false,
								size: 70,
								arrowPosition: ARROW_POSITION.RIGHT,
								toolbarComponent: "ConsoleToolbar",
								contentComponent: "ConsoleContent",
								renderWhenCollapsed: false
							}
						]
					},
					{
						type: LAYOUT_TYPE.SPLITTER,
						id: "layoutSplitterRight"
					},
					{
						type: LAYOUT_TYPE.TABS,
						id: "right",
						size: "250px",
						tabs: [
							{
								id: "components",
								label: "Components",
								contentComponent: "ComponentPalette",
								renderWhenHidden: false
							},
							{
								id: "inspector",
								label: "Inspector",
								contentComponent: "Inspector"
							}
						]
					}
				]
			}
		});
	}, []);

	const previewViewportConfig = useMemo(
		() => ({
			width: "100%",
			height: "100%",
			zoom: 1.0,
			showBoundaries: false,
			showSafeArea: false
		}),
		[]
	);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "Empty": {
				return null;
			}
			case "OutlineToolbar": {
				return (
					<>
						<ToolbarTitle>Outline</ToolbarTitle>
					</>
				);
			}
			case "OutlineContent": {
				return <OutlineContent />;
			}
			case "DataExplorerToolbar": {
				return (
					<>
						<ToolbarTitle>Data Explorer</ToolbarTitle>
					</>
				);
			}
			case "DataExplorerContent": {
				return <DataExplorerContent />;
			}
			case "ConsoleToolbar": {
				return (
					<>
						<TestModuleConsoleToolbar />
					</>
				);
			}
			case "ConsoleContent": {
				return <TestModuleConsoleContent />;
			}
			case "ComponentPalette": {
				return <div>Component Palette</div>;
			}
			case "Inspector": {
				return <TestModuleInspector />;
			}
			case "PreviewContent": {
				return (
					<ViewPreview
						renderContext={props.view.getTestRenderContext()}
						viewportConfig={previewViewportConfig}
					/>
				);
			}
			case "PreviewToolbar": {
				return (
					<>
						<Breadcrumbs items={breadcrumbs} />

						<ToolbarSpacer />

						<ToolbarButton
							icon="mdi/dock-left"
							active={activeButtons.has("a")}
							onClick={() => toggleButton("a")}
						/>
						<ToolbarButton
							icon="mdi/dock-right"
							active={activeButtons.has("b")}
							onClick={() => toggleButton("b")}
						/>

						<ToolbarSpacer />

						<ToolbarButton
							icon="mdi/television"
							active={activeButtons.has("c")}
							onClick={() => toggleButton("c")}
						/>
						<ToolbarButton
							icon="mdi/desktop-mac"
							active={activeButtons.has("d")}
							onClick={() => toggleButton("d")}
						/>
						<ToolbarButton
							icon="mdi/cellphone"
							active={activeButtons.has("e")}
							onClick={() => toggleButton("e")}
						/>
						<ToolbarButton
							icon="mdi/cellphone"
							active={activeButtons.has("f")}
							onClick={() => toggleButton("f")}
						/>

						<ToolbarSpacer />

						<ToolbarButton icon="mdi/content-save" />
						<ToolbarButton icon="mdi/backup-restore" disabled />
						<ToolbarButton icon="mdi/delete" />

						<ToolbarSpacer />

						<ToolbarButton icon="mdi/history" />

						<ToolbarSpacer />

						<ToolbarButton icon="mdi/undo-variant" />
						<ToolbarButton icon="mdi/redo-variant" disabled />
					</>
				);
			}
			case "ToastsToolbar": {
				return (
					<>
						<ToolbarTitle>Toasts</ToolbarTitle>

						<ToolbarCommandButton
							icon="mdi/information"
							label="Info"
							commandId="test.toast.info"
							commandArgs={[ "Info Message", "Lorem ipsum dolor sit amet." ]}
						/>
						<ToolbarCommandButton
							icon="mdi/check"
							label="Success"
							commandId="test.toast.success"
							commandArgs={[ "Success Message" ]}
						/>
						<ToolbarCommandButton
							icon="mdi/alert"
							label="Warning"
							commandId="test.toast.warning"
							commandArgs={[ "Warning Message", "Lorem ipsum dolor sit amet." ]}
						/>
						<ToolbarCommandButton
							icon="mdi/alert-circle"
							label="Error"
							commandId="test.toast.error"
							commandArgs={[ "Error Message", "Lorem ipsum dolor sit amet." ]}
						/>
						<ToolbarCommandButton
							icon="mdi/motion-play"
							label="Loading"
							commandId="test.toast.loading"
							commandArgs={[ "Info Message", "Lorem ipsum dolor sit amet." ]}
						/>
						<ToolbarCommandButton
							icon="mdi/motion-play"
							label="Loading #2"
							commandId="test.toast.loading2"
						/>
						<ToolbarCommandButton
							icon="mdi/motion-play"
							label="Loading #3"
							commandId="test.toast.loading3"
						/>
					</>
				);
			}
			case "PrimaryPanelToolbar": {
				return (
					<>
						<ToolbarTitle>Primary Panel</ToolbarTitle>
					</>
				);
			}
			case "PrimaryPanel": {
				return <TestModulePrimaryPanel />;
			}
		}
	};

	return <Layout state={layoutState} renderComponent={renderComponent} />;
};
